import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormField } from '@/components/molecules/FormField';
import Button from '@/components/atoms/Button';
import Card from '@/components/atoms/Card';
import { apiService } from '@/services/api';
import { validatePassword, validatePasswordMatch } from '@/utils/validation';
import '@/components/organisms/LoginForm/LoginForm.scss';
import './Login.scss';

/**
 * Step 2 of password recovery: the link in the email opens this page with
 * ?token=…; without it, the person pastes the token from the email.
 */
const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const tokenFromLink = searchParams.get('token') || '';
  const [token, setToken] = useState(tokenFromLink);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<{ token?: string; password?: string; confirmPassword?: string; general?: string }>({});
  const [doneMessage, setDoneMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors = {
      token: token.trim() ? undefined : t('auth.resetPassword.tokenRequired'),
      password: validatePassword(password, t) || undefined,
      confirmPassword: validatePasswordMatch(password, confirmPassword, t) || undefined,
    };
    setErrors(nextErrors);
    if (nextErrors.token || nextErrors.password || nextErrors.confirmPassword) {
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiService.resetPassword({ token: token.trim(), new_password: password });
      setDoneMessage(response.message || t('auth.resetPassword.done'));
    } catch (requestError: any) {
      setErrors({ general: requestError.response?.data?.detail || t('auth.resetPassword.error') });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-page__content">
        <Card variant="elevated" padding="lg" className="login-form">
          <div className="login-form__header">
            <h2 className="login-form__title">{t('auth.resetPassword.title')}</h2>
            <p className="login-form__subtitle">{t('auth.resetPassword.subtitle')}</p>
          </div>

          {doneMessage ? (
            <>
              <p className="login-form__notice" role="status">{doneMessage}</p>
              <div className="login-form__actions">
                <Link to="/login" className="login-page__register-link">
                  {t('auth.resetPassword.goToLogin')}
                </Link>
              </div>
            </>
          ) : (
            <form onSubmit={handleSubmit} className="login-form__form">
              {errors.general && <p className="login-form__notice login-form__notice--error" role="alert">{errors.general}</p>}
              {!tokenFromLink && (
                <FormField
                  id="reset-password-token"
                  label={t('auth.resetPassword.token')}
                  type="text"
                  name="token"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  error={errors.token}
                  helperText={t('auth.resetPassword.tokenHelp')}
                  required
                  autoComplete="one-time-code"
                />
              )}
              <FormField
                id="reset-password-new"
                label={t('auth.resetPassword.newPassword')}
                type="password"
                name="new_password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={errors.password}
                required
                autoComplete="new-password"
              />
              <FormField
                id="reset-password-confirm"
                label={t('auth.resetPassword.confirmPassword')}
                type="password"
                name="confirm_password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={errors.confirmPassword}
                required
                autoComplete="new-password"
              />
              <div className="login-form__actions">
                <Button type="submit" variant="primary" size="md" fullWidth loading={isLoading} disabled={isLoading}>
                  {t('auth.resetPassword.submit')}
                </Button>
              </div>
            </form>
          )}

          <div className="login-page__footer">
            <p className="login-page__home-text">
              <Link to="/forgot-password" className="login-page__home-link">
                {t('auth.resetPassword.requestNewLink')}
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
